/**
 * Node View Options Menu ("⋮" → "This Node")
 * Rendered via a portal to document.body with position:fixed, explicit screen-pixel
 * coordinates — same pattern as MoreOptionsMenu. Keeping it outside any pan/zoom-transformed
 * ancestor means no scale/pan math is needed, and it can always be clamped to stay fully
 * inside the visible canvas even when the node itself sits at an edge/corner.
 *
 * Placement: prefers directly below the trigger button (right-edge aligned with it). Flips
 * above when there isn't room below, shifts to left-edge alignment when right-alignment would
 * run past the left boundary, then a final clamp guarantees full visibility regardless.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCanvasMenuBounds, clampMenuPosition } from '../../utils/canvasMenuBounds';

interface NodeViewOptionsMenuProps {
  /** The "⋮" trigger button — anchor used to position and to keep the dropdown inside the canvas viewport. */
  anchorEl: HTMLElement | null;
  portsHidden: boolean;
  satellitesHidden: boolean;
  componentsHidden: boolean;
  isCollapsed: boolean;
  onToggleHidePorts: () => void;
  onToggleHideSatellites: () => void;
  onToggleHideComponents: () => void;
  onHideNode: () => void;
  onToggleCollapse: () => void;
  onReset: () => void;
  onClose: () => void;
}

interface FixedPosition {
  top: number;
  left: number;
}

const GAP = 4;
const MARGIN = 10; // safe spacing from visible canvas boundaries (8-12px)

export const NodeViewOptionsMenu: React.FC<NodeViewOptionsMenuProps> = ({
  anchorEl,
  portsHidden,
  satellitesHidden,
  componentsHidden,
  isCollapsed,
  onToggleHidePorts,
  onToggleHideSatellites,
  onToggleHideComponents,
  onHideNode,
  onToggleCollapse,
  onReset,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  // null until the first layout measurement — menu renders invisible (but measurable) until then.
  const [pos, setPos] = useState<FixedPosition | null>(null);

  useLayoutEffect(() => {
    const recalc = () => {
      const menuEl = menuRef.current;
      if (!menuEl || !anchorEl) return;

      const anchorRect = anchorEl.getBoundingClientRect();
      const menuRect = menuEl.getBoundingClientRect();
      const bounds = getCanvasMenuBounds(anchorEl);

      // Preferred: directly below the button, right-edge aligned with the button's right edge.
      let top = anchorRect.bottom + GAP;
      let left = anchorRect.right - menuRect.width;

      // Not enough room below — flip upward, but only if there's actually more room above;
      // otherwise keep it below and let the final clamp keep the menu on-screen.
      if (top + menuRect.height + MARGIN > bounds.bottom) {
        const flippedTop = anchorRect.top - menuRect.height - GAP;
        if (flippedTop >= bounds.top) top = flippedTop;
      }

      // Right-aligned placement would run past the left boundary — shift to hug the button's
      // left edge instead (opens toward the right) when that actually fits.
      if (left < bounds.left + MARGIN) {
        const shiftedLeft = anchorRect.left;
        if (shiftedLeft + menuRect.width + MARGIN <= bounds.right) left = shiftedLeft;
      }

      // Final safety clamp — guarantees the full menu stays inside the visible canvas even in
      // corner cases (e.g. a node pinned at an extreme edge, or a viewport narrower than the menu).
      setPos(clampMenuPosition({ top, left }, menuRect, bounds, MARGIN));
    };

    recalc();
    window.addEventListener('resize', recalc);

    // Panning via drag is already caught by the outside-mousedown handler below (it closes the
    // menu on drag-start). Scroll/trackpad panning doesn't fire mousedown, so close on wheel too
    // rather than leaving a stale, now-misaligned menu floating over the canvas.
    const canvasEl = anchorEl?.closest('.react-flow');
    canvasEl?.addEventListener('wheel', onClose, { passive: true });

    return () => {
      window.removeEventListener('resize', recalc);
      canvasEl?.removeEventListener('wheel', onClose);
    };
  }, [anchorEl, onClose]);

  // Close when clicking anywhere outside this menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      // The "⋮" button that opens this menu already toggles open/closed in its own onClick.
      // This listener runs on mousedown in the capture phase (fires first), so if it also
      // closed here, the button's own click handler would run right after and immediately
      // reopen it, making the button look like it never closes.
      if ((target as HTMLElement)?.closest?.('[data-view-menu-trigger]')) return;
      onClose();
    };
    // Capture phase: React Flow's internal d3-zoom/drag calls stopImmediatePropagation() on
    // pointerdown during pan/drag gesture detection, which would otherwise stop this listener
    // from ever firing in the bubble phase when the click originates on the canvas pane.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [onClose]);

  const itemStyle = (active?: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '8px',
    width: '100%', padding: '7px 12px',
    background: 'transparent', border: 'none',
    color: active ? '#fff' : 'rgba(255,255,255,0.75)',
    fontSize: '11px', fontWeight: active ? 600 : 400,
    cursor: 'pointer', textAlign: 'left',
    transition: 'background 0.1s',
  });

  const dot = (on: boolean) => (
    <span style={{
      width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
      background: on ? '#309B48' : 'transparent',
      border: `1.5px solid ${on ? '#309B48' : 'rgba(255,255,255,0.3)'}`,
    }} />
  );

  const menuStyle: React.CSSProperties = {
    // Fixed + portaled to document.body — coordinates are plain screen pixels, computed and
    // clamped in the useLayoutEffect above.
    position: 'fixed',
    top: pos ? `${pos.top}px` : '0px',
    left: pos ? `${pos.left}px` : '0px',
    // Hidden until the first measurement resolves a position, so there's no visible flash/jump.
    visibility: pos ? 'visible' : 'hidden',
    background: '#1e293b',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
    minWidth: '185px',
    overflow: 'hidden',
    zIndex: 9999,
  };

  const handle = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

  return createPortal(
    <div
      ref={menuRef}
      className="nodrag nopan"
      data-testid="node-view-options-menu"
      style={menuStyle}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
        This Node
      </div>

      {/* Hide Unused Ports — per-node toggle */}
      <button
        className="nodrag"
        style={itemStyle(portsHidden)}
        onClick={handle(onToggleHidePorts)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {dot(portsHidden)}
        {portsHidden ? 'Show Unused Ports' : 'Hide Unused Ports'}
      </button>

      {/* Hide THIS node's satellites (direct + indirect) */}
      <button
        className="nodrag"
        style={itemStyle(satellitesHidden)}
        onClick={handle(onToggleHideSatellites)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {dot(satellitesHidden)}
        {satellitesHidden ? 'Show My Satellites' : 'Hide My Satellites'}
      </button>

      {/* Hide THIS node's component nodes */}
      <button
        className="nodrag"
        style={itemStyle(componentsHidden)}
        onClick={handle(onToggleHideComponents)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {dot(componentsHidden)}
        {componentsHidden ? 'Show My Components' : 'Hide My Components'}
      </button>

      {/* Hide this node — revealed only via Reset All or Show All */}
      <button
        className="nodrag"
        style={{ ...itemStyle(false), color: 'rgba(251,191,36,0.85)' }}
        onClick={handle(onHideNode)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(251,191,36,0.08)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        title="Hide this node. Use 'Show All Satellites' or 'Reset All' to reveal it."
      >
        <span style={{
          width: '8px', height: '8px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', lineHeight: 1,
        }}>⊘</span>
        Hide Me
      </button>

      {/* Divider */}
      <div style={{ margin: '4px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }} />

      {/* Collapse / Expand */}
      <button
        className="nodrag"
        style={itemStyle(isCollapsed)}
        onClick={handle(onToggleCollapse)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {dot(isCollapsed)}
        {isCollapsed ? '▼  Expand' : '▲  Collapse'}
      </button>

      {/* Reset — only resets this node's local view state */}
      <button
        className="nodrag"
        style={{ ...itemStyle(false), color: 'rgba(239,68,68,0.8)' }}
        onClick={handle(onReset)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.08)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <span style={{
          width: '8px', height: '8px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', lineHeight: 1,
        }}>↺</span>
        Reset This Node
      </button>
    </div>,
    document.body
  );
};
